import type { EventKind } from '@shkola/api';
import type { SubjectIcon } from '@shkola/design';
import { type CSSProperties, type ReactNode, useCallback, useEffect, useId, useState } from 'react';
import { DEEP, GRAD, type Grad, INK } from './ShapesArt';

/*
 * The pictures of «Форми 2» (28.10, his «эталон» — the ManyChat illustrations; the mascot will be drawn
 * the same way later): a thing drawn in one loose ink line over one soft shape, a dot of colour, a loop
 * or an arrow where something moves. His edit of 28.10: the colours only from our gradients — a shape is
 * the deep gradient of the colour of the card it stands on (the same hue, a notch deeper, so it never
 * blends in and never clashes), a second shape the softer one. A picture for each subject, each kind of
 * task and of event, the moods, the faces of the avatar. Decoration only: hidden from screen readers.
 */

/** One line: the ink, round ends. */
const line = {
  fill: 'none',
  stroke: INK,
  strokeWidth: 2.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** The paints of a picture: the deep and the soft gradient of its colour, a dot of the full tone. */
interface Paint {
  deep: string;
  soft: string;
  dot: string;
}

/** A small arrowhead at (x, y) pointing at the angle `a` (degrees). */
function Head({ x, y, a }: { x: number; y: number; a: number }) {
  return <path d="M-6 -4.5L0 0L-6 4.5" transform={`translate(${x} ${y}) rotate(${a})`} {...line} />;
}

/**
 * The frame of a picture: a 64×64 box with the gradients of its colour. A `ghost` (his «крупней, на
 * фоне, не бросается в глаза, в градиенте», 28.10) is the big quiet picture behind a card's words: the
 * line in a gradient of the card's own ink fading out, the shapes a notch softer.
 */
function Pic({
  tone,
  size,
  ghost = false,
  className = '',
  children,
}: {
  tone: Grad;
  size: number;
  ghost?: boolean;
  className?: string;
  children: (p: Paint) => ReactNode;
}) {
  const id = `la${useId().replace(/:/g, '')}`;
  const [a, b] = DEEP[tone];
  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      aria-hidden
      focusable="false"
      className={`shrink-0 overflow-visible ${ghost ? 'art-ghost' : ''} ${className}`}
      style={ghost ? ({ '--sh-ink': `url(#${id}l)` } as CSSProperties) : undefined}
    >
      <defs>
        <linearGradient id={`${id}d`} x1="0.1" y1="0" x2="0.9" y2="1">
          <stop offset="0" stopColor={ghost ? GRAD[tone].to : a} />
          <stop offset="1" stopColor={ghost ? a : b} />
        </linearGradient>
        <linearGradient id={`${id}s`} x1="0.1" y1="0" x2="0.9" y2="1">
          <stop offset="0" stopColor={GRAD[tone].to} stopOpacity={ghost ? 0.6 : 1} />
          <stop offset="1" stopColor={a} stopOpacity={ghost ? 0.6 : 1} />
        </linearGradient>
        {ghost && (
          <linearGradient id={`${id}l`} x1="0.9" y1="0" x2="0.1" y2="1">
            <stop offset="0" stopColor={GRAD[tone].ink} stopOpacity="0.55" />
            <stop offset="1" stopColor={GRAD[tone].ink} stopOpacity="0.12" />
          </linearGradient>
        )}
      </defs>
      {children({ deep: `url(#${id}d)`, soft: `url(#${id}s)`, dot: b })}
    </svg>
  );
}

// —— the subjects ——

const SUBJECT: Record<SubjectIcon, (p: Paint) => ReactNode> = {
  // algebra, mathematics: π over a sun
  Sigma: (p) => (
    <>
      <circle cx="40" cy="26" r="15" fill={p.deep} />
      <path d="M9 24C9 18 14 16 20 17L53 16" {...line} />
      <path d="M24 17C24 30 22 41 16 48" {...line} />
      <path d="M41 16C40 30 41 42 48 46C52 48 56 45 55 41" {...line} />
    </>
  ),
  // geometry: a set square, a compass arc going round it
  Triangle: (p) => (
    <>
      <path
        d="M12 50L34 14L54 50Z"
        fill={p.deep}
        stroke={p.deep}
        strokeWidth="5"
        strokeLinejoin="round"
      />
      <path d="M8 34C6 18 22 6 38 8C46 9 52 13 55 19" {...line} />
      <Head x={55} y={19} a={62} />
      <circle cx="34" cy="38" r="3" fill={INK} />
    </>
  ),
  // physics: a nucleus and its orbits
  Atom: (p) => (
    <>
      <circle cx="32" cy="32" r="19" fill={p.soft} />
      <ellipse cx="32" cy="32" rx="27" ry="10" transform="rotate(28 32 32)" {...line} />
      <ellipse cx="32" cy="32" rx="27" ry="10" transform="rotate(-28 32 32)" {...line} />
      <circle cx="32" cy="32" r="5.5" fill={p.deep} />
      <circle cx="56" cy="44" r="3.2" fill={INK} />
    </>
  ),
  // astronomy: a moon, a star, a path
  Telescope: (p) => (
    <>
      <circle cx="24" cy="28" r="15" fill={p.deep} />
      <path
        d="M46 10C47 17 49 19 56 20C49 21 47 23 46 30C45 23 43 21 36 20C43 19 45 17 46 10Z"
        {...line}
      />
      <path d="M6 54C16 50 18 40 28 42C36 44 34 54 44 52C50 51 54 46 56 42" {...line} />
      <Head x={56} y={42} a={-62} />
    </>
  ),
  // chemistry: a flask with something bubbling
  FlaskConical: (p) => (
    <>
      <path d="M17 42H47L52 52Q54 57 48 57H16Q10 57 12 52Z" fill={p.deep} />
      <path d="M26 8V26L12 52Q10 57 16 57H48Q54 57 52 52L38 26V8" {...line} />
      <path d="M22 8H42" {...line} />
      <circle cx="40" cy="36" r="3.4" fill={p.dot} />
      <circle cx="29" cy="31" r="2.6" {...line} strokeWidth={2.2} />
    </>
  ),
  // biology: a leaf on a blob
  Leaf: (p) => (
    <>
      <ellipse cx="38" cy="26" rx="18" ry="13" transform="rotate(-35 38 26)" fill={p.deep} />
      <path d="M14 50C14 26 30 12 52 12C52 34 38 50 14 50Z" {...line} />
      <path d="M14 50C26 38 36 28 46 18" {...line} />
      <path d="M14 50C10 54 8 58 12 60" {...line} />
    </>
  ),
  // nature: a sprout in a pot
  Sprout: (p) => (
    <>
      <path d="M18 40H46L42 58H22Z" fill={p.deep} />
      <path d="M32 40C32 30 30 24 32 16" {...line} />
      <path d="M32 27C24 27 17 21 17 14C25 14 32 19 32 27Z" {...line} />
      <path d="M32 22C38 18 46 18 48 11C40 9 34 14 32 22Z" {...line} />
    </>
  ),
  // geography: a globe with a path round it
  Earth: (p) => (
    <>
      <circle cx="32" cy="30" r="18" fill={p.deep} />
      <ellipse cx="32" cy="30" rx="8" ry="18" {...line} />
      <path d="M14 30H50" {...line} />
      <path d="M6 42C4 54 30 58 52 46C60 42 60 36 55 32" {...line} />
      <Head x={55} y={32} a={-115} />
    </>
  ),
  // world history: a folded map, a route to a point
  Map: (p) => (
    <>
      <path d="M10 18L24 12L40 18L54 12V46L40 52L24 46L10 52Z" fill={p.deep} />
      <path d="M10 18L24 12L40 18L54 12V46L40 52L24 46L10 52Z" {...line} />
      <path d="M24 12V46M40 18V52" {...line} strokeWidth={2} />
      <path d="M15 42C21 32 31 42 44 26" {...line} strokeDasharray="1 5" />
      <circle cx="45" cy="25" r="3.4" fill={INK} />
    </>
  ),
  // history: a temple of columns
  Landmark: (p) => (
    <>
      <rect x="12" y="22" width="40" height="30" rx="4" fill={p.deep} />
      <path d="M8 22L32 8L56 22Z" {...line} />
      <path d="M17 27V47M27 27V47M37 27V47M47 27V47" {...line} />
      <path d="M8 52H56" {...line} />
      <circle cx="32" cy="16" r="2.6" fill={INK} />
    </>
  ),
  // world literature: books on a shelf
  Library: (p) => (
    <>
      <rect x="10" y="14" width="12" height="38" rx="3" fill={p.deep} />
      <rect
        x="36"
        y="18"
        width="10"
        height="34"
        rx="3"
        fill={p.soft}
        transform="rotate(14 41 35)"
      />
      <path d="M10 14H22V52H10ZM24 10H34V52H24Z" {...line} />
      <path d="M38 17L48 19.5L42 54L32 51.5" {...line} />
      <path d="M6 54H58" {...line} />
    </>
  ),
  // literature: an open book
  BookOpen: (p) => (
    <>
      <path d="M32 18C24 12 14 12 8 14V50C14 48 24 48 32 52Z" fill={p.deep} />
      <path d="M32 18C40 12 50 12 56 14V50C50 48 40 48 32 52Z" fill={p.soft} />
      <path
        d="M32 18C24 12 14 12 8 14V50C14 48 24 48 32 52C40 48 50 48 56 50V14C50 12 40 12 32 18Z"
        {...line}
      />
      <path d="M32 18V52" {...line} />
    </>
  ),
  // a closed book (any other subject)
  BookMarked: (p) => (
    <>
      <rect x="14" y="10" width="36" height="44" rx="5" fill={p.deep} />
      <path d="M14 15A5 5 0 0 1 19 10H50V54H19A5 5 0 0 1 14 49Z" {...line} />
      <path d="M14 46H50" {...line} />
      <path d="M36 10V26L40.5 22L45 26V10" {...line} fill="#FFFFFF" />
    </>
  ),
  // a foreign language: two speech bubbles, one says something
  Languages: (p) => (
    <>
      <path
        d="M14 10H38A8 8 0 0 1 46 18V28A8 8 0 0 1 38 36H24L16 44V36H14A8 8 0 0 1 6 28V18A8 8 0 0 1 14 10Z"
        fill={p.deep}
      />
      <path
        d="M26 22H50A8 8 0 0 1 58 30V40A8 8 0 0 1 50 48H48V56L40 48H26A8 8 0 0 1 18 40V30A8 8 0 0 1 26 22Z"
        {...line}
      />
      <circle cx="31" cy="35" r="2.2" fill={INK} />
      <circle cx="38" cy="35" r="2.2" fill={INK} />
      <circle cx="45" cy="35" r="2.2" fill={INK} />
    </>
  ),
  // the mother tongue: a pen nib writing a looping line
  PenLine: (p) => (
    <>
      <circle cx="44" cy="18" r="12" fill={p.deep} />
      <path d="M44 6L58 20L36 42L22 44L24 30Z" {...line} />
      <path d="M24 42L35 31" {...line} strokeWidth={2} />
      <path d="M22 45C16 51 8 50 9 56C10 61 19 56 23 58C29 61 35 54 42 57" {...line} />
    </>
  ),
  // informatics: a screen that says </>
  Monitor: (p) => (
    <>
      <rect x="11" y="15" width="42" height="26" rx="3" fill={p.deep} />
      <rect x="8" y="12" width="48" height="32" rx="5" {...line} />
      <path d="M26 52H38M32 44V52" {...line} />
      <path d="M23 23L18 28L23 33M41 23L46 28L41 33M35 21L29 35" {...line} strokeWidth={2.3} />
    </>
  ),
  // music: two notes
  Music: (p) => (
    <>
      <ellipse cx="26" cy="46" rx="8" ry="6.5" fill={p.soft} />
      <ellipse cx="48" cy="40" rx="8" ry="6.5" fill={p.deep} />
      <ellipse cx="26" cy="46" rx="8" ry="6.5" {...line} />
      <ellipse cx="48" cy="40" rx="8" ry="6.5" {...line} />
      <path d="M33 45V12L55 7V39" {...line} />
      <path d="M33 20L55 15" {...line} />
    </>
  ),
  // art: a palette, a brush
  Palette: (p) => (
    <>
      <path
        d="M30 10C14 10 6 22 8 34C10 46 22 54 30 50C36 47 32 40 38 38C46 36 54 40 54 28C54 16 44 10 30 10Z"
        fill={p.deep}
      />
      <circle cx="21" cy="25" r="4.5" fill="#FFFFFF" />
      <circle cx="33" cy="19" r="4.5" fill={p.soft} />
      <circle cx="20" cy="38" r="4" {...line} strokeWidth={2.2} />
      <path d="M60 8L40 32" {...line} />
      <path d="M40 32C35 34 34 39 30 42C36 44 42 40 42 34Z" fill={INK} />
    </>
  ),
  // technologies: a hammer swinging down
  Hammer: (p) => (
    <g transform="rotate(-32 32 34)">
      <rect x="16" y="10" width="32" height="14" rx="4" fill={p.deep} />
      <rect x="16" y="10" width="32" height="14" rx="4" {...line} />
      <path d="M32 24V58" {...line} />
      <path d="M52 30C58 34 58 42 54 46M58 26C66 34 64 46 58 52" {...line} strokeWidth={2.2} />
    </g>
  ),
  // defence of Ukraine: a shield with a tick
  Shield: (p) => (
    <>
      <path d="M32 8L52 14V30C52 44 42 52 32 56C22 52 12 44 12 30V14Z" fill={p.deep} />
      <path d="M32 8L52 14V30C52 44 42 52 32 56C22 52 12 44 12 30V14Z" {...line} />
      <path d="M23 31L30 38L43 24" {...line} />
    </>
  ),
  // health: a heart, a pulse
  HeartPulse: (p) => (
    <>
      <path
        d="M32 52C14 40 8 30 12 20C16 12 28 12 32 22C36 12 48 12 52 20C56 30 50 40 32 52Z"
        fill={p.deep}
      />
      <path d="M4 34H18L23 24L30 44L36 28L40 34H60" {...line} />
    </>
  ),
  // law, ethics, economics: scales
  Scale: (p) => (
    <>
      <path d="M4 34C6 42 18 42 20 34Z" fill={p.deep} />
      <path d="M44 34C46 42 58 42 60 34Z" fill={p.soft} />
      <path d="M12 18H52M32 12V54M22 54H42" {...line} />
      <path d="M12 18L4 34H20ZM52 18L44 34H60Z" {...line} strokeWidth={2.2} />
      <circle cx="32" cy="12" r="3" fill={INK} />
    </>
  ),
  // physical education: a ball spinning (ref. the ManyChat ball)
  Dumbbell: (p) => (
    <>
      <ellipse
        cx="32"
        cy="34"
        rx="29"
        ry="10"
        transform="rotate(-14 32 34)"
        fill="none"
        stroke={p.soft}
        strokeWidth="5"
      />
      <circle cx="32" cy="32" r="16" fill={p.deep} />
      <path d="M16 32H48M32 16C25 23 25 41 32 48M32 16C39 23 39 41 32 48" {...line} />
      <path d="M4 40C8 48 20 50 30 49" {...line} />
      <Head x={30} y={49} a={-4} />
    </>
  ),
};

export function SubjectArt({
  icon,
  tone,
  size = 44,
  ghost,
  className,
}: {
  icon: SubjectIcon;
  /** the colour of the card it stands on */
  tone: Grad;
  size?: number;
  ghost?: boolean;
  className?: string;
}) {
  return (
    <Pic tone={tone} size={size} ghost={ghost} className={className}>
      {SUBJECT[icon] ?? SUBJECT.BookMarked}
    </Pic>
  );
}

// —— the kinds of what to do ——

export type TaskArtKind = 'overdue' | 'soon' | 'tests' | 'rework' | 'ack' | 'catchUp' | 'done';

const TASK: Record<TaskArtKind, (p: Paint) => ReactNode> = {
  // late: an alarm clock ringing
  overdue: (p) => (
    <>
      <circle cx="32" cy="34" r="17" fill={p.deep} />
      <circle cx="32" cy="34" r="17" {...line} />
      <path d="M32 34V24M32 34L40 38" {...line} />
      <path d="M14 20C11 15 16 9 22 12M50 20C53 15 48 9 42 12" {...line} />
      <path d="M22 50L18 57M42 50L46 57" {...line} />
      <path d="M6 30L2 28M58 30L62 28M8 40L3 42M56 40L61 42" {...line} strokeWidth={2.2} />
    </>
  ),
  // soon: a paper plane with its looping trail
  soon: (p) => (
    <>
      <path d="M22 28L60 8L48 52L36 38Z" fill={p.deep} />
      <path d="M22 28L60 8L48 52L36 38ZM36 38L60 8" {...line} />
      <path d="M34 40C26 46 18 42 21 36C24 30 30 38 22 48C16 56 8 54 4 60" {...line} />
      <circle cx="4" cy="60" r="3" fill={INK} />
    </>
  ),
  // the tests: a sheet with an A+
  tests: (p) => (
    <>
      <rect x="20" y="12" width="34" height="44" rx="5" fill={p.deep} />
      <rect x="12" y="6" width="34" height="44" rx="5" {...line} fill="#FFFFFF" />
      <path d="M19 32L24 18L29 32M21 27H27" {...line} />
      <path d="M36 21V29M32 25H40" {...line} />
      <path d="M19 40H38" {...line} strokeWidth={2.2} />
    </>
  ),
  // to redo: a sheet and an arrow going round it
  rework: (p) => (
    <>
      <rect x="20" y="16" width="26" height="32" rx="4" fill={p.deep} />
      <path d="M50 18C42 8 22 8 14 20C6 32 12 50 28 54C42 58 54 48 54 36" {...line} />
      <Head x={50} y={18} a={-130} />
      <path d="M26 26H40M26 33H40M26 40H34" {...line} strokeWidth={2.2} />
    </>
  ),
  // to confirm: a megaphone
  ack: (p) => (
    <>
      <path d="M12 26L40 12V50L12 36Z" fill={p.deep} />
      <path d="M12 26L40 12V50L12 36Z" {...line} />
      <path d="M18 37L22 52H29L26 40" {...line} />
      <path d="M47 24C51 28 51 34 47 38M53 18C60 26 60 36 53 44" {...line} />
    </>
  ),
  // to catch up: a flag ahead
  catchUp: (p) => (
    <>
      <path d="M18 10C28 4 36 16 48 10V30C36 36 28 24 18 30Z" fill={p.deep} />
      <path d="M18 56V8M18 10C28 4 36 16 48 10V30C36 36 28 24 18 30" {...line} />
      <path d="M4 58C14 50 26 60 36 52C42 47 48 50 54 46" {...line} strokeDasharray="1 5" />
      <circle cx="56" cy="45" r="3.2" fill={INK} />
    </>
  ),
  // all done: a loop into a tick
  done: (p) => (
    <>
      <circle cx="38" cy="30" r="18" fill={p.deep} />
      <path d="M6 40C12 34 20 36 24 44C26 48 30 48 33 42C38 32 46 22 58 14" {...line} />
      <path d="M12 54C18 50 22 52 24 56" {...line} strokeWidth={2.2} />
    </>
  ),
};

export function TaskArt({
  kind,
  tone,
  size = 72,
  ghost,
  className,
}: {
  kind: TaskArtKind;
  tone: Grad;
  size?: number;
  ghost?: boolean;
  className?: string;
}) {
  return (
    <Pic tone={tone} size={size} ghost={ghost} className={className}>
      {TASK[kind]}
    </Pic>
  );
}

// —— the kinds of events ——

const EVENT: Record<EventKind, (p: Paint) => ReactNode> = {
  // a trip: a bus on its way (ref. the ManyChat car)
  trip: (p) => (
    <>
      <rect x="6" y="16" width="50" height="28" rx="9" fill={p.deep} />
      <path d="M6 30H56M22 16V30M38 16V30" {...line} strokeWidth={2.2} />
      <circle cx="18" cy="46" r="6" {...line} fill="#FFFFFF" />
      <circle cx="44" cy="46" r="6" {...line} fill="#FFFFFF" />
      <path d="M58 22C62 24 62 30 58 32" {...line} />
    </>
  ),
  // a contest: a cup
  exam: (p) => (
    <>
      <path d="M20 10H44L42 30C40 38 24 38 22 30Z" fill={p.deep} />
      <path d="M20 10H44L42 30C40 38 24 38 22 30Z" {...line} />
      <path d="M20 14C10 14 10 28 22 28M44 14C54 14 54 28 42 28" {...line} />
      <path d="M32 37V46M23 52H41" {...line} />
      <path
        d="M54 40C55 44 56 45 60 46C56 47 55 48 54 52C53 48 52 47 48 46C52 45 53 44 54 40Z"
        fill={INK}
      />
    </>
  ),
  // a celebration: two balloons on their strings (ref. the ManyChat balloon)
  celebration: (p) => (
    <>
      <ellipse cx="22" cy="22" rx="12" ry="14" fill={p.soft} />
      <ellipse cx="40" cy="20" rx="13" ry="15" fill={p.deep} />
      <path d="M40 35C38 42 44 44 40 50C36 56 28 52 30 60" {...line} />
      <path d="M22 36C22 42 18 44 20 50" {...line} strokeWidth={2.2} />
    </>
  ),
  // a meeting: two people talking
  meeting: (p) => (
    <>
      <circle cx="44" cy="22" r="10" fill={p.deep} />
      <circle cx="22" cy="24" r="9" {...line} />
      <path d="M6 54C6 42 14 36 22 36C30 36 36 42 36 50" {...line} />
      <path d="M34 40C38 36 48 36 54 40C58 44 58 50 58 54" {...line} />
      <circle cx="44" cy="22" r="10" {...line} />
    </>
  ),
  // the holidays: a sun
  holiday: (p) => (
    <>
      <circle cx="32" cy="32" r="14" fill={p.deep} />
      <path
        d="M32 6V12M32 52V58M6 32H12M52 32H58M13 13L17.5 17.5M46.5 46.5L51 51M51 13L46.5 17.5M17.5 46.5L13 51"
        {...line}
      />
    </>
  ),
  // anything else: a star with its path
  other: (p) => (
    <>
      <circle cx="40" cy="26" r="14" fill={p.deep} />
      <path
        d="M32 10C34 22 38 26 50 28C38 30 34 34 32 46C30 34 26 30 14 28C26 26 30 22 32 10Z"
        {...line}
      />
      <path d="M8 56C14 50 22 54 28 50" {...line} strokeWidth={2.2} />
    </>
  ),
};

export function EventArt({
  kind,
  tone,
  size = 52,
  ghost,
  className,
}: {
  kind: EventKind;
  tone: Grad;
  size?: number;
  ghost?: boolean;
  className?: string;
}) {
  return (
    <Pic tone={tone} size={size} ghost={ghost} className={className}>
      {EVENT[kind]}
    </Pic>
  );
}

// —— the moods: a circle in the mood's colour, a face in one line ——

export type MoodKind = 'great' | 'good' | 'okay' | 'sad' | 'angry' | 'anxious';

export const MOOD_TONE: Record<MoodKind, Grad> = {
  great: 'lime',
  good: 'mint',
  okay: 'butter',
  sad: 'sky',
  angry: 'coral',
  anxious: 'lavender',
};

/** A mood's face; with no mood — the face that asks, on white in a dashed ring (a place to fill). */
export function MoodArt({ mood, size = 40 }: { mood: MoodKind | undefined; size?: number }) {
  const eye = { r: 2.8, fill: INK };
  if (!mood)
    return (
      <Pic tone="lavender" size={size}>
        {() => (
          <>
            <circle cx="32" cy="32" r="30" fill="#FFFFFF" />
            <circle cx="32" cy="32" r="28.5" {...line} strokeWidth={2.2} strokeDasharray="3 5" />
            <circle cx="25" cy="29" {...eye} />
            <circle cx="39" cy="29" {...eye} />
            <path d="M25 41Q28.5 38 32 41T39 41" {...line} />
          </>
        )}
      </Pic>
    );
  return (
    <Pic tone={MOOD_TONE[mood]} size={size}>
      {(p) => (
        <>
          <circle cx="32" cy="32" r="30" fill={p.deep} />
          {mood === 'great' ? (
            <path d="M20 28Q24 23 28 28M36 28Q40 23 44 28M21 37Q32 49 43 37" {...line} />
          ) : (
            <>
              <circle cx="25" cy="29" {...eye} />
              <circle cx="39" cy="29" {...eye} />
              {mood === 'good' && <path d="M23 38Q32 46 41 38" {...line} />}
              {mood === 'okay' && <path d="M24 40H40" {...line} />}
              {mood === 'sad' && <path d="M23 44Q32 36 41 44" {...line} />}
              {mood === 'angry' && (
                <path d="M19 21L28 25M45 21L36 25M24 43Q32 37 40 43" {...line} />
              )}
              {mood === 'anxious' && (
                <path
                  d="M19 24L28 21M45 24L36 21M21 41Q23.75 38 26.5 41T32 41T37.5 41T43 41"
                  {...line}
                />
              )}
            </>
          )}
        </>
      )}
    </Pic>
  );
}

// —— the faces to pick for an avatar ——

export const FACES = [
  'curls',
  'glasses',
  'cap',
  'headphones',
  'spring',
  'profile',
  'stars',
  'bun',
] as const;
export type Face = (typeof FACES)[number] | 'initials';
export const ALL_FACES: ReadonlyArray<Face> = ['initials', ...FACES];

/** The colour of each face's round ground. */
const FACE_TONE: Record<Face, Grad> = {
  initials: 'lavender',
  curls: 'apricot',
  glasses: 'sky',
  cap: 'mint',
  headphones: 'pink',
  spring: 'lime',
  profile: 'aqua',
  stars: 'butter',
  bun: 'coral',
};

const FACE: Record<Exclude<Face, 'initials'>, (p: Paint) => ReactNode> = {
  curls: (p) => (
    <>
      <path
        d="M17 26C17 16 26 10 34 13C38 7 50 10 48 20C54 22 52 30 48 30"
        {...line}
        fill={p.deep}
      />
      <path d="M16 30C16 46 25 54 34 54C42 54 48 48 48 38" {...line} />
      <path d="M24 33Q27 30 30 33M36 33Q39 30 42 33" {...line} strokeWidth={2.3} />
      <path d="M27 43C30 47 36 47 39 43" {...line} />
    </>
  ),
  glasses: (p) => (
    <>
      <path d="M14 26C20 14 42 10 50 24" {...line} />
      <path d="M15 30C15 46 24 54 32 54C40 54 49 46 49 30" {...line} />
      <circle cx="25" cy="34" r="6" {...line} strokeWidth={2.3} fill={p.deep} />
      <circle cx="40" cy="34" r="6" {...line} strokeWidth={2.3} fill={p.deep} />
      <path d="M31 34H34" {...line} strokeWidth={2.3} />
      <path d="M28 45H37" {...line} />
    </>
  ),
  cap: (p) => (
    <>
      <path d="M12 22L32 13L52 22L32 31Z" {...line} fill={p.deep} />
      <path d="M52 22V33" {...line} />
      <circle cx="52" cy="35" r="2.6" fill={INK} />
      <path d="M18 30C18 46 26 54 32 54C38 54 46 46 46 30" {...line} />
      <circle cx="26" cy="38" r="2.2" fill={INK} />
      <circle cx="38" cy="38" r="2.2" fill={INK} />
      <path d="M28 45C30 47.5 34 47.5 36 45" {...line} />
    </>
  ),
  headphones: (p) => (
    <>
      <path d="M13 36C13 18 51 18 51 36" {...line} />
      <rect x="9" y="33" width="9" height="14" rx="4" fill={p.deep} />
      <rect x="46" y="33" width="9" height="14" rx="4" fill={p.deep} />
      <path d="M20 40C20 50 26 55 32 55C38 55 44 50 44 40" {...line} />
      <path d="M25 37Q28 40 31 37M34 37Q37 40 40 37" {...line} strokeWidth={2.3} />
      <path d="M28 47C30 49 34 49 36 47" {...line} />
    </>
  ),
  spring: () => (
    <>
      <path
        d="M12 28C12 20 20 20 20 26C20 18 28 18 28 24C28 16 36 16 36 23C36 16 44 17 44 25C44 18 52 20 52 28"
        {...line}
      />
      <path d="M16 34C16 48 24 55 32 55C40 55 48 48 48 34" {...line} />
      <circle cx="26" cy="38" r="2.3" fill={INK} />
      <circle cx="38" cy="38" r="2.3" fill={INK} />
      <ellipse cx="32" cy="46" rx="3" ry="3.5" {...line} strokeWidth={2.2} />
    </>
  ),
  profile: (p) => (
    <>
      <circle cx="48" cy="18" r="6" fill={p.deep} />
      <path
        d="M20 8C30 9 38 16 38 24C38 28 44 31 41 35C39 37 37 37 38 40C39 43 36 44 37 47C38 51 33 53 28 52C24 51 22 54 23 58"
        {...line}
      />
      <path d="M20 8C12 12 8 22 12 32C14 38 18 42 18 50" {...line} />
      <circle cx="31" cy="24" r="2.3" fill={INK} />
    </>
  ),
  stars: (p) => (
    <>
      <path
        d="M15 28C15 46 24 54 32 54C40 54 49 46 49 28C49 16 40 10 32 10C24 10 15 16 15 28Z"
        {...line}
      />
      <path
        d="M25 29C25.6 32 26.4 32.8 29 33.5C26.4 34.2 25.6 35 25 38C24.4 35 23.6 34.2 21 33.5C23.6 32.8 24.4 32 25 29ZM39 29C39.6 32 40.4 32.8 43 33.5C40.4 34.2 39.6 35 39 38C38.4 35 37.6 34.2 35 33.5C37.6 32.8 38.4 32 39 29Z"
        fill={p.dot}
      />
      <path d="M25 43C28 48 36 48 39 43" {...line} />
    </>
  ),
  bun: (p) => (
    <>
      <circle cx="32" cy="11" r="7" fill={p.deep} />
      <path d="M15 32C15 20 22 15 32 15C42 15 49 20 49 32" {...line} />
      <path d="M15 32C15 47 24 54 32 54C40 54 49 47 49 32" {...line} />
      <path d="M24 35Q27 32 30 35M34 35Q37 32 40 35" {...line} strokeWidth={2.3} />
      <path d="M28 44C30 46.5 34 46.5 36 44" {...line} />
    </>
  ),
};

/** A face on its round ground in the soft gradient of its colour; `initials` — the letters. */
export function FaceAvatar({
  face,
  initials,
  size = 64,
}: {
  face: Face;
  initials: string;
  size?: number;
}) {
  return (
    <Pic tone={FACE_TONE[face]} size={size}>
      {(p) => (
        <>
          <circle cx="32" cy="32" r="32" fill={p.soft} />
          {face === 'initials' ? (
            <text
              x="32"
              y="33"
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="22"
              fontWeight="500"
              letterSpacing="-0.5"
              fill={INK}
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {initials}
            </text>
          ) : (
            FACE[face](p)
          )}
        </>
      )}
    </Pic>
  );
}

const FACE_EVENT = 'shkola:face';

/** The face the student picked, kept in this browser (no photos of children); every avatar follows it. */
export function useFace(memberId: string) {
  const key = `shkola.face.${memberId}`;
  const read = useCallback((): Face => {
    try {
      const saved = localStorage.getItem(key);
      if (saved && (ALL_FACES as string[]).includes(saved)) return saved as Face;
    } catch {
      // private mode: the initials
    }
    return 'initials';
  }, [key]);
  const [face, setFace] = useState<Face>(read);
  useEffect(() => {
    const sync = () => setFace(read());
    window.addEventListener(FACE_EVENT, sync);
    return () => window.removeEventListener(FACE_EVENT, sync);
  }, [read]);
  const choose = (next: Face) => {
    setFace(next);
    try {
      localStorage.setItem(key, next);
    } catch {
      // only for this visit
    }
    window.dispatchEvent(new Event(FACE_EVENT));
  };
  return [face, choose] as const;
}
